/**
 * When an alert should fire.
 *
 * Pure so the cron's behaviour can be tested at any hour of any day rather than only at 7 AM.
 */

import { gtfsWeekdayIndex, minutesSinceMidnight, serviceDateKey } from "../shared/time";

/** Which reminder fired, e.g. `lead-15`. Derived from the lead so any number of them works. */
export type AlertKind = string;

export function leadKind(minutesBefore: number): AlertKind {
	return `lead-${minutesBefore}`;
}

/**
 * Fixed, because these are about a train that is already running rather than a routine being
 * planned. Five minutes is time to reach the platform; one minute is time to stop reading.
 *
 * Spoken in the app only. A notification about a train he is already standing next to would be
 * telling him what he can see.
 */
export const TRAIN_LEAD_MINUTES = [5, 1];

export function trainKind(minutesBefore: number): AlertKind {
	return `train-${minutesBefore}`;
}

/** What a platform reminder says. No "arriving now": the feed is not that trustworthy. */
export function trainAlertText(minutesBefore: number): string {
	return `your train arrives in ${minutesBefore} ${minutesBefore === 1 ? "minute" : "minutes"}`;
}

/** Inside this the margin at the station is close enough to add to reminder copy. */
export const SPARE_WITHIN_MINUTES = 5;

/**
 * How a reminder describes the leave window, in the words the card is using at that moment,
 * so what he hears and what he reads cannot disagree.
 *
 * One number, counting to the moment he has to start moving. A range read as two answers to the
 * same question, and "within the next" was only ever there to say which end of one was meant.
 */
export function leaveWindowText(minutesBefore: number): string {
	if (minutesBefore <= 0) {
		return "it's time to leave";
	}
	return `leave in ${minutesBefore} ${minutesBefore === 1 ? "minute" : "minutes"}`;
}

/** A reminder this many minutes before the moment he has to be walking out. */
export interface AlertLead {
	minutesBefore: number;
	enabled: boolean;
}

export interface AlertConfig {
	enabled: boolean;
	/** Which commute to watch. Only Rockridge-bound profiles carry a deadline. */
	profileId: string;
	/** Preferred arrival at the final destination, minutes after local midnight. */
	targetMinutes: number;
	/** GTFS weekday indices: 0 = Monday .. 6 = Sunday. */
	days: number[];
	/** Any number of reminders; the app offers three. Leave-now is always sent regardless. */
	leads: AlertLead[];
	/** Reminders on the platform, which carry on after the leave reminders have stopped. */
	trainAlerts: boolean;
	/** In-app chimes and spoken reminders. System notification sound is controlled by the OS. */
	soundEnabled: boolean;
	/**
	 * Where the drive starts. The cron has no live GPS, so without this it can only use the
	 * profile's typical duration; with it, alerts get the same traffic-aware routing the app has.
	 */
	origin: { lat: number; lon: number } | null;
	/**
	 * The two timings the rider can move, carried so the cron computes the leave time his card
	 * shows rather than the profile's default. Null means the profile's, which is what every
	 * subscription saved before this said.
	 */
	earlyToleranceMinutes: number | null;
	lateToleranceMinutes: number | null;
	egressSeconds: number | null;
	prepSeconds: number | null;
	stationBufferSeconds: number | null;
}

/** The reminder that is not optional: it is the whole point of the app. */
const LEAVE_NOW_LEAD = 0;
export const DEFAULT_LEADS: AlertLead[] = [
	{ minutesBefore: 0, enabled: true },
	{ minutesBefore: 10, enabled: true },
	{ minutesBefore: 30, enabled: true },
];

export const DEFAULT_ALERT_CONFIG: AlertConfig = {
	enabled: false,
	profileId: "civic-am",
	targetMinutes: 8 * 60 + 10,
	days: [0, 1, 2, 3, 4],
	leads: DEFAULT_LEADS,
	trainAlerts: true,
	soundEnabled: true,
	origin: null,
	earlyToleranceMinutes: null,
	lateToleranceMinutes: null,
	egressSeconds: null,
	prepSeconds: null,
	stationBufferSeconds: null,
};

/** What to remove when the preferred notification transport says its address is permanently gone. */
export function goneTransportAction(hasApns: boolean): "clear-apns" | "delete-subscription" {
	// An APNs token belongs to an app installation and is replaceable. The schedule belongs to the
	// rider and survives it; the app repairs the token on its next launch.
	return hasApns ? "clear-apns" : "delete-subscription";
}

/** Minutes before the deadline that should fire, most urgent first. */
export function activeLeadMinutes(config: AlertConfig): number[] {
	const chosen = config.leads.filter((lead) => lead.enabled).map((lead) => lead.minutesBefore);
	return [...new Set([LEAVE_NOW_LEAD, ...chosen])].sort((a, b) => a - b);
}

/**
 * How long before the target the cron starts doing real work.
 *
 * Generous enough to cover drive, ride, buffers and a badly delayed train, so that outside it
 * we can skip fetching anything at all.
 */
const ACTIVE_WINDOW_LEAD_MINUTES = 100;
const ACTIVE_WINDOW_TRAIL_MINUTES = 15;

/**
 * How long before the target the morning counts as begun.
 *
 * Wider than the sweep, because the two answer different questions. The sweep's lead is sized to
 * cover the journey itself, which left it opening barely half an hour before the rider actually
 * has to leave — so a phone picked up while the coffee brews sat in `planning` with a perfect fix
 * of the kitchen. Being at home costs nothing to believe and nothing to act on.
 */
const HOME_WINDOW_LEAD_MINUTES = 200;

/** An alert this far past its moment is stale; the morning has moved on. */
const STALE_AFTER_MINUTES = 20;

export function isAlertDay(nowMs: number, timezone: string, days: number[]): boolean {
	return days.includes(gtfsWeekdayIndex(serviceDateKey(nowMs, timezone)));
}

function isWithinWindow(nowMs: number, timezone: string, targetMinutes: number, leadMinutes: number): boolean {
	const minutes = minutesSinceMidnight(nowMs, timezone);
	const start = targetMinutes - leadMinutes;
	const end = targetMinutes + ACTIVE_WINDOW_TRAIL_MINUTES;
	if (start >= 0) {
		return minutes >= start && minutes <= end;
	}
	// Window straddles midnight.
	return minutes >= start + 1440 || minutes <= end;
}

/**
 * Cheap gate for the every-minute cron: true only when a departure could plausibly be near.
 * Derived from the target time alone, so it costs no network and no schedule lookup.
 */
export function isWithinActiveWindow(nowMs: number, timezone: string, targetMinutes: number): boolean {
	return isWithinWindow(nowMs, timezone, targetMinutes, ACTIVE_WINDOW_LEAD_MINUTES);
}

/** Whether the morning has begun, which is the only evidence `at_home` ever needs. */
export function isWithinHomeWindow(nowMs: number, timezone: string, targetMinutes: number): boolean {
	return isWithinWindow(nowMs, timezone, targetMinutes, HOME_WINDOW_LEAD_MINUTES);
}

/**
 * The stretch of the day the ride home can happen in.
 *
 * A judgement, not a measurement, and deliberately wide: school lets out mid-afternoon and the
 * last plausible pickup is well after dark. It exists only to stop the app raising a pickup at
 * breakfast, so being generous costs nothing.
 *
 * Not derived from `targetMinutes` — that is the *morning* arrival, and gating the evening on it
 * meant Pickup Mode could only ever open between 04:45 and 08:20, which is to say never.
 */
const RIDE_HOME_FROM_MINUTES = 11 * 60;
const RIDE_HOME_UNTIL_MINUTES = 22 * 60;

export function isWithinRideHomeWindow(nowMs: number, timezone: string): boolean {
	const minutes = minutesSinceMidnight(nowMs, timezone);
	return minutes >= RIDE_HOME_FROM_MINUTES && minutes <= RIDE_HOME_UNTIL_MINUTES;
}

export interface AlertDecision {
	kind: AlertKind;
	/** How far ahead of its moment this reminder is, so the message can say so. */
	minutesBefore: number;
	/** The moment the alert became due, for logging and dedupe. */
	dueMs: number;
	/**
	 * Reminders this one has overtaken, to be recorded as sent alongside it.
	 *
	 * Only one alert goes out per tick, so without this the next tick would find the earlier
	 * moments still unsent and deliver them after the urgent one — "leave now", then a minute
	 * later, "leave in 10 to 15 minutes".
	 */
	supersedes: AlertKind[];
}

export interface AlertDecisionInput {
	nowMs: number;
	timezone: string;
	config: AlertConfig;
	/** Latest comfortable moment to leave the apartment. */
	departureDeadlineMs: number | null;
	/** The app has observed this device leave its origin today. */
	traveling: boolean;
	/** Kinds already sent for today's service date. */
	alreadySent: AlertKind[];
}

/**
 * How early a push may go out.
 *
 * The cron only runs once a minute, so a reminder sent no earlier than its moment arrives up to
 * a minute after it. A full minute of head start turns that around: it now lands somewhere in
 * the minute before, which is the right side to be wrong on, and gets it there ahead of the
 * spoken reminder rather than behind it.
 */
export const PUSH_HEAD_START_MS = 60_000;

/**
 * The alert due right now, if any.
 *
 * The most urgent unsent reminder wins: if the app was asleep and several moments have passed,
 * the rider needs "leave now", not a stale nudge to start getting ready. Later moments are
 * more urgent, which is also what puts a platform reminder ahead of a leave one.
 *
 * Leaving stops the reminders about leaving, for obvious reasons, but not the ones about the
 * train he is on his way to catch.
 */
export function dueAlert(input: AlertDecisionInput): AlertDecision | null {
	const { config, nowMs } = input;
	if (!config.enabled || !isAlertDay(nowMs, input.timezone, config.days)) {
		return null;
	}

	const candidates: Array<Omit<AlertDecision, "supersedes">> = [];
	if (input.departureDeadlineMs != null && !input.traveling) {
		for (const minutesBefore of activeLeadMinutes(config)) {
			candidates.push({
				kind: leadKind(minutesBefore),
				minutesBefore,
				dueMs: input.departureDeadlineMs - minutesBefore * 60_000,
			});
		}
	}

	const staleBefore = nowMs - STALE_AFTER_MINUTES * 60_000;
	const sent = new Set(input.alreadySent);
	const due = candidates
		.filter(
			(candidate) =>
				nowMs >= candidate.dueMs - PUSH_HEAD_START_MS && candidate.dueMs >= staleBefore && !sent.has(candidate.kind),
		)
		.sort((a, b) => b.dueMs - a.dueMs)[0];
	if (!due) {
		return null;
	}

	return {
		...due,
		// Anything whose moment is already behind this one has been overtaken by it.
		supersedes: candidates.filter((other) => other.dueMs < due.dueMs && !sent.has(other.kind)).map((other) => other.kind),
	};
}

/** A reminder the open app should speak, as opposed to one the cron should push. */
export interface SpokenReminder {
	minutesBefore: number;
	category: "leave" | "train";
	dueMs: number;
}

export interface SpokenReminderInput {
	nowMs: number;
	/** The moment he has to be walking out. Null when there is no travel leg to plan. */
	leaveByMs: number | null;
	/** Predicted departure of the train the alerts are describing. */
	boardingMs: number;
	/** Minutes before the deadline that are in force, mirroring the push schedule. */
	leadMinutes: number[];
	trainAlerts: boolean;
	/** Leaving stops the reminders about leaving, but not the ones about the train. */
	traveling: boolean;
}

/**
 * How long after its moment a reminder is still worth saying out loud.
 *
 * Past this the moment has been overtaken: a later reminder is either already due or the thing
 * itself has happened, and hearing "leave in fifteen minutes" at the deadline is worse than
 * silence.
 */
const SPEAK_WITHIN_MS = 60_000;

/**
 * The reminder to speak right now, if any.
 *
 * The push is sent a minute early and speaks on arrival, so it normally gets there first. This
 * speaks at the moment itself, for when it does not. Later moments are the more urgent ones,
 * which is what puts a train reminder ahead of a leave reminder when both have come due while
 * the phone was asleep.
 */
export function dueSpokenReminder(input: SpokenReminderInput): SpokenReminder | null {
	const due: SpokenReminder[] = [];
	if (input.leaveByMs != null && !input.traveling) {
		for (const minutesBefore of input.leadMinutes) {
			due.push({ minutesBefore, category: "leave", dueMs: input.leaveByMs - minutesBefore * 60_000 });
		}
	}
	if (input.trainAlerts) {
		for (const minutesBefore of TRAIN_LEAD_MINUTES) {
			due.push({ minutesBefore, category: "train", dueMs: input.boardingMs - minutesBefore * 60_000 });
		}
	}
	return (
		due
			.filter((entry) => input.nowMs >= entry.dueMs && input.nowMs - entry.dueMs < SPEAK_WITHIN_MS)
			.sort((a, b) => b.dueMs - a.dueMs)[0] ?? null
	);
}

export function validateAlertConfig(value: unknown, allowedProfileIds: string[]): AlertConfig | null {
	if (!value || typeof value !== "object") {
		return null;
	}
	const raw = value as Record<string, unknown>;
	const profileId = typeof raw.profileId === "string" ? raw.profileId : null;
	if (!profileId || !allowedProfileIds.includes(profileId)) {
		return null;
	}
	const targetMinutes = Number(raw.targetMinutes);
	if (!Number.isInteger(targetMinutes) || targetMinutes < 0 || targetMinutes >= 1440) {
		return null;
	}
	const days = Array.isArray(raw.days) ? raw.days.filter((day): day is number => Number.isInteger(day) && day >= 0 && day <= 6) : [];

	return {
		enabled: raw.enabled !== false,
		profileId,
		targetMinutes,
		days: [...new Set(days)].sort((a, b) => a - b),
		leads: parseLeads(raw.leads),
		// Platform chimes are always on; old saved false values are intentionally ignored.
		trainAlerts: true,
		soundEnabled: raw.soundEnabled !== false,
		origin: parseOrigin(raw.origin),
		earlyToleranceMinutes: seconds(raw.earlyToleranceMinutes, 40),
		lateToleranceMinutes: seconds(raw.lateToleranceMinutes, 20),
		egressSeconds: seconds(raw.egressSeconds, 1200),
		prepSeconds: seconds(raw.prepSeconds, 900),
		stationBufferSeconds: seconds(raw.stationBufferSeconds, 1800),
	};
}

/** Absent, or out of the range the sliders offer, means the profile's own figure. */
function seconds(value: unknown, max: number): number | null {
	if (value == null) {
		return null;
	}
	const parsed = Number(value);
	return Number.isFinite(parsed) && parsed >= 0 && parsed <= max ? Math.round(parsed) : null;
}

/** Older subscriptions predate configurable reminders, so they inherit the defaults. */
function parseLeads(value: unknown): AlertLead[] {
	if (!Array.isArray(value)) {
		return DEFAULT_LEADS;
	}
	const seen = new Set<number>();
	const leads: AlertLead[] = [];
	for (const entry of value) {
		if (!entry || typeof entry !== "object") {
			continue;
		}
		const raw = entry as Record<string, unknown>;
		const minutesBefore = Number(raw.minutesBefore);
		if (!Number.isInteger(minutesBefore) || minutesBefore < 0 || minutesBefore > 120 || seen.has(minutesBefore)) {
			continue;
		}
		seen.add(minutesBefore);
		leads.push({ minutesBefore, enabled: raw.enabled !== false });
	}
	return leads.sort((a, b) => a.minutesBefore - b.minutesBefore);
}

function parseOrigin(value: unknown): { lat: number; lon: number } | null {
	if (!value || typeof value !== "object") {
		return null;
	}
	const raw = value as Record<string, unknown>;
	const lat = Number(raw.lat);
	const lon = Number(raw.lon);
	if (!Number.isFinite(lat) || !Number.isFinite(lon) || Math.abs(lat) > 90 || Math.abs(lon) > 180) {
		return null;
	}
	return { lat, lon };
}
